import type { Article } from "../hooks/useArticles";
import { parseDateToTimestamp } from "./dateHelpers";

/** Minúsculas, sin tildes ni signos, espacios colapsados: "Aerolíneas" → "aerolineas". */
export function normalizeSearchText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9ñ]+/g, " ")
    .trim();
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/&[a-z#0-9]+;/gi, " ");
}

/** Todo el texto por el que se puede encontrar una noticia, ya normalizado. */
export function buildSearchText(article: Partial<Article>): string {
  const tags = Array.isArray(article.tags) ? article.tags : [];
  const fields = [
    article.title,
    article.excerpt,
    article.category,
    article.subcategory,
    ...tags,
    article.author,
    article.date,
    article.slug?.replace(/-/g, " "),
    article.created_at?.slice(0, 10),
    article.is_international ? "internacional" : "",
    ...(Array.isArray(article.image_captions) ? article.image_captions : []),
    article.content ? stripHtml(article.content) : "",
  ];
  return " " + normalizeSearchText(fields.filter(Boolean).join(" ")) + " ";
}

/** Quita el plural para que "pasaportes" también encuentre "pasaporte" (y viceversa). */
function singular(word: string): string {
  if (word.length < 5) return word;
  if (word.endsWith("es")) return word.slice(0, -2);
  if (word.endsWith("s")) return word.slice(0, -1);
  return word;
}

/** Palabras de la búsqueda, normalizadas. Vacío = no hay búsqueda. */
export function searchTerms(query: string): string[] {
  return normalizeSearchText(query).split(" ").filter(Boolean).map(singular);
}

function timestamp(value: number): number {
  return isNaN(value) ? 0 : value;
}

/** Más nueva primero: por fecha visible y, dentro del mismo día, por hora de subida. */
function compareNewestFirst(a: Partial<Article> | undefined, b: Partial<Article> | undefined): number {
  const dayA = timestamp(a?.date ? parseDateToTimestamp(a.date) : 0);
  const dayB = timestamp(b?.date ? parseDateToTimestamp(b.date) : 0);
  if (dayA !== dayB) return dayB - dayA;
  const createdA = timestamp(a?.created_at ? new Date(a.created_at).getTime() : 0);
  const createdB = timestamp(b?.created_at ? new Date(b.created_at).getTime() : 0);
  return createdB - createdA;
}

/** Coincide si TODAS las palabras aparecen en algún campo (en cualquier orden). */
export function matchesSearchText(searchText: string, terms: string[]): boolean {
  return terms.every((t) => searchText.includes(t));
}

/**
 * Filtra noticias por cualquier criterio y ordena los resultados de la más nueva a la más antigua.
 * `getArticle` permite filtrar NewsItem usando el artículo completo.
 */
export function filterBySearch<T>(
  items: T[],
  query: string,
  getArticle: (item: T) => Partial<Article> | undefined,
): T[] {
  const terms = searchTerms(query);
  if (terms.length === 0) return items;
  return items
    .filter((item) => {
      const article = getArticle(item);
      return article ? matchesSearchText(buildSearchText(article), terms) : false;
    })
    .sort((a, b) => compareNewestFirst(getArticle(a), getArticle(b)));
}
